import React, { useState,useEffect } from 'react'
import { Button, Col, Row, Card, Form, Modal } from "react-bootstrap-v5";
import masterService from '../../../../Services/masterService'
import FileViewer from 'react-file-viewer';
import { AiOutlineFileDone } from 'react-icons/ai'
import { ImCancelCircle } from 'react-icons/im'
import FileReader from './FileReader'
import JobService from "../../../../Services/JobService"
import { toast } from 'react-toastify';

const REACT_APP_API_BASEURL = process.env.REACT_APP_API_BASEURL

const documentViewUrl = (filePath) => {
    if (!filePath) return ''
    const base = String(REACT_APP_API_BASEURL || '').replace(/\/$/, '')
    if (filePath.startsWith(base) || filePath.startsWith('/uploads/')) {
        return filePath.startsWith('/') ? `${base}${filePath}` : filePath
    }
    return `${base}/retailer/document/view?key=${encodeURIComponent(filePath)}`
}

const invoiceTitle = (type) => {
    if (type === 'insurance') return 'INS INVOICE'
    if (type === 'packing') return 'PACKING LIST'
    return 'CUSTOMS INVOICE'
}

const jobProducts = (job) => {
    if (Array.isArray(job?.custom_products) && job.custom_products.length) return job.custom_products
    if (Array.isArray(job?.quote_request_items) && job.quote_request_items.length) return job.quote_request_items
    return []
}

const money = (value) => {
    const number = Number(value)
    return Number.isFinite(number) ? number : 0
}

const amountWords = (value) => {
    const ones = ['', 'ONE', 'TWO', 'THREE', 'FOUR', 'FIVE', 'SIX', 'SEVEN', 'EIGHT', 'NINE']
    const teens = ['TEN', 'ELEVEN', 'TWELVE', 'THIRTEEN', 'FOURTEEN', 'FIFTEEN', 'SIXTEEN', 'SEVENTEEN', 'EIGHTEEN', 'NINETEEN']
    const tens = ['', '', 'TWENTY', 'THIRTY', 'FORTY', 'FIFTY', 'SIXTY', 'SEVENTY', 'EIGHTY', 'NINETY']
    const chunk = (num) => {
        if (num < 10) return ones[num]
        if (num < 20) return teens[num - 10]
        if (num < 100) return `${tens[Math.floor(num / 10)]}${num % 10 ? ' ' + ones[num % 10] : ''}`.trim()
        if (num < 1000) return `${ones[Math.floor(num / 100)]} HUNDRED${num % 100 ? ' ' + chunk(num % 100) : ''}`.trim()
        if (num < 100000) return `${chunk(Math.floor(num / 1000))} THOUSAND${num % 1000 ? ' ' + chunk(num % 1000) : ''}`.trim()
        if (num < 10000000) return `${chunk(Math.floor(num / 100000))} LAKH${num % 100000 ? ' ' + chunk(num % 100000) : ''}`.trim()
        return `${chunk(Math.floor(num / 10000000))} CRORE${num % 10000000 ? ' ' + chunk(num % 10000000) : ''}`.trim()
    }
    const rounded = Math.round(money(value))
    return rounded ? chunk(rounded) : 'ZERO'
}

const formatBookingDate = (value) => {
    if (!value) return ''
    const date = new Date(Number(value) || value)
    if (Number.isNaN(date.getTime())) return ''
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    return `${day}/${month}/${date.getFullYear()}`
}

const applyBookingIdentity = (draft, job) => ({
    ...draft,
    invoiceNo: draft?.invoiceNo || job?.job_token || '',
    invoiceDate: draft?.invoiceDate || formatBookingDate(job?.created_at) || '',
})

const buildInvoiceDraft = (job, type) => {
    const products = jobProducts(job)
    const actualRows = Array.isArray(job?.quote_request_items) ? job.quote_request_items : []
    const port = String(job?.shipment_details?.delivery_type || '').toUpperCase().includes('PORT')
    let cursor = 0
    const items = products.map((product) => {
        const rows = Array.isArray(product.packages) && product.packages.length ? product.packages : [null]
        rows.forEach(() => {
            cursor += 1
        })
        const qty = Number(product.quantity) || 1
        const unitPrice = money(product.unitPrice || product.price || product.product_price || (product.totalValue ? Number(product.totalValue) / qty : 0))
        return {
            description: String(product.productName || product.product_name || 'COMMERCIAL GOODS').toUpperCase(),
            pkgNote: '',
            hsCode: product.hsCode || product.hs_code || '',
            coo: product.coo || job?.shipment_details?.origin_country || 'UAE',
            qty,
            unitPrice,
        }
    })
    const packages = []
    cursor = 0
    products.forEach((product) => {
        const description = String(product.productName || product.product_name || 'COMMERCIAL GOODS').toUpperCase()
        const rows = Array.isArray(product.packages) && product.packages.length ? product.packages : [null]
        const partStart = packages.length
        rows.forEach((pkg) => {
            const actual = actualRows[cursor]?.actual || {}
            cursor += 1
            const count = Number(actual.no_of_pkgs || pkg?.quantity || pkg?.count || 1) || 1
            const weight = actual.kilogram ?? pkg?.weight ?? 0
            const length = actual.length ?? pkg?.length ?? 0
            const width = actual.width ?? pkg?.width ?? 0
            const height = actual.height ?? pkg?.height ?? 0
            for (let index = 0; index < count; index += 1) {
                packages.push({
                    marks: `PKG ${packages.length + 1}`,
                    weight: `${weight} KGS`,
                    dimension: `${length} x ${width} x ${height} CMS`,
                    description,
                })
            }
        })
        const partCount = packages.length - partStart
        if (partCount > 1) {
            packages.slice(partStart).forEach((row, index) => {
                row.description = `PART ${index + 1} OF ${description}`
            })
        }
    })
    const itemsTotal = items.reduce((sum, item) => sum + (money(item.qty) * money(item.unitPrice)), 0)
    const insurance = money(job?.add_ons?.insurance?.insurance_amount || job?.payment_information?.breakdown?.insurance)
    const freightCart = money(job?.payment_information?.actual_amount)
    let freight = Math.min(freightCart, itemsTotal * 0.2)
    if (itemsTotal > 0 || freightCart > 0) freight = Math.max(freight, 100)
    return {
        shipperName: job?.customer_details?.name || job?.sender_details?.full_name || job?.general_details?.retailer_name || '',
        shipperAddress: job?.sender_details?.address || 'DUBAI',
        shipperCountry: job?.shipment_details?.origin_country || 'UAE',
        shipperPhone: job?.customer_details?.mobile_number || job?.sender_details?.phone_number || '',
        invoiceNo: job?.job_token || '',
        invoiceDate: formatBookingDate(job?.created_at),
        consigneeName: job?.receiver_details?.receiver_name || job?.receiver_details?.consignee || '',
        consigneeAddress: job?.receiver_details?.address || '',
        consigneeCity: job?.receiver_details?.city || '',
        consigneeCountry: job?.receiver_details?.country || job?.shipment_details?.destination_country || '',
        consigneePhone: job?.receiver_details?.pickup_contact || '',
        consigneeEmail: job?.receiver_details?.email || '',
        shippingMode: job?.shipment_details?.shipment_mode || '',
        deliveryType: port ? 'DOOR TO PORT' : 'DOOR TO DOOR',
        terms: port ? 'CIF' : 'DDP',
        paymentTerms: 'PREPAID',
        personalUse: 'FOR PERSONAL USE ONLY',
        items: items.length ? items : [{ description: 'COMMERCIAL GOODS', pkgNote: '', hsCode: '', coo: 'UAE', qty: 1, unitPrice: money(job?.shipment_details?.value_of_goods) }],
        packages,
        insurance,
        freight,
        destinationCharges: type === 'insurance' ? Math.max(0, freightCart - freight) : 0,
        coverageNote: 'All Risk Comprehensive',
        declaredValue: '',
    }
}

const sheetInput = {
    width: '100%',
    border: '1px dashed #94a3b8',
    background: '#fff',
    fontSize: '0.72rem',
    padding: '2px 4px',
    fontFamily: 'Arial, sans-serif',
}

const InvoiceSheet = ({ draft, type, onChange, editing }) => {
    const setField = (field, value) => onChange({ ...draft, [field]: value })
    const Field = ({ value, change, align = 'left' }) => (
        editing
            ? <input style={{ ...sheetInput, textAlign: align }} value={value ?? ''} onChange={(event) => change(event.target.value)} />
            : <div style={{ fontSize: '0.72rem', textAlign: align, minHeight: 14, whiteSpace: 'pre-wrap' }}>{value}</div>
    )
    const itemsTotal = (draft.items || []).reduce((sum, item) => sum + (money(item.qty) * money(item.unitPrice)), 0)
    const insurance = money(draft.insurance)
    const freight = money(draft.freight)
    const destinationCharges = money(draft.destinationCharges)
    const totalCif = itemsTotal + insurance + freight
    const totalDdp = totalCif + destinationCharges
    const grandTotal = type === 'insurance' ? totalDdp : totalCif
    const cell = { border: '1px solid #000', padding: '2px 4px', fontSize: '0.68rem', lineHeight: 1.2 }
    const packingRows = (draft.packages || []).map((row, index, rows) => {
        const name = String(row.description || '').replace(/^PART \d+ OF /, '')
        const same = rows.filter((item) => String(item.description || '').replace(/^PART \d+ OF /, '') === name)
        if (same.length < 2) return row
        const part = rows.slice(0, index + 1).filter((item) => String(item.description || '').replace(/^PART \d+ OF /, '') === name).length
        return { ...row, description: `PART ${part} OF ${name}` }
    })
    const totalPackages = packingRows.length
    const totalGross = packingRows.reduce((sum, row) => sum + money(String(row.weight || '').replace(/[^0-9.]/g, '')), 0)
    return (
        <div id="export-invoice-sheet" style={{ width: '210mm', minHeight: '297mm', margin: '0 auto', background: '#fff', color: '#111', fontFamily: 'Arial, sans-serif', boxSizing: 'border-box' }}>
            <div style={{ border: '2px solid #000', minHeight: '297mm', boxSizing: 'border-box' }}>
                <div style={{ padding: '4px 6px', textAlign: 'center', borderBottom: '2px solid #000' }}>
                    <div style={{ fontSize: '0.95rem', fontWeight: 800, color: '#003366' }}>{invoiceTitle(type)}</div>
                </div>
                <div style={{ display: 'flex', borderBottom: '2px solid #000' }}>
                    <div style={{ flex: 1, borderRight: '2px solid #000', padding: '4px 6px', fontSize: '0.72rem' }}>
                        <strong>SHIPPER:</strong>
                        <Field value={draft.shipperName || ''} change={(value) => setField('shipperName', value)} />
                        <Field value={draft.shipperAddress || ''} change={(value) => setField('shipperAddress', value)} />
                        <Field value={draft.shipperCountry || ''} change={(value) => setField('shipperCountry', value)} />
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>PH: <Field value={draft.shipperPhone || ''} change={(value) => setField('shipperPhone', value)} /></div>
                    </div>
                    <div style={{ flex: 1 }}>
                        <div style={{ padding: '4px 6px', borderBottom: '2px solid #000', fontWeight: 700, fontSize: '0.72rem', display: 'flex', alignItems: 'center', gap: 4 }}>INVOICE NO: <Field value={draft.invoiceNo || ''} change={(value) => setField('invoiceNo', value)} /></div>
                        <div style={{ padding: '4px 6px', borderBottom: '2px solid #000', fontWeight: 700, fontSize: '0.72rem', display: 'flex', alignItems: 'center', gap: 4 }}>INVOICE DT: <Field value={draft.invoiceDate || ''} change={(value) => setField('invoiceDate', value)} /></div>
                    </div>
                </div>
                <div style={{ display: 'flex', borderBottom: '2px solid #000' }}>
                    <div style={{ flex: 1, borderRight: '2px solid #000', padding: '4px 6px', fontSize: '0.72rem' }}>
                        <strong>CONSIGNEE:</strong>
                        <Field value={draft.consigneeName || ''} change={(value) => setField('consigneeName', value)} />
                        <Field value={draft.consigneeAddress || ''} change={(value) => setField('consigneeAddress', value)} />
                        <Field value={draft.consigneeCity || ''} change={(value) => setField('consigneeCity', value)} />
                        <Field value={draft.consigneeCountry || ''} change={(value) => setField('consigneeCountry', value)} />
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>PH: <Field value={draft.consigneePhone || ''} change={(value) => setField('consigneePhone', value)} /></div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>E: <Field value={draft.consigneeEmail || ''} change={(value) => setField('consigneeEmail', value)} /></div>
                    </div>
                    <div style={{ flex: 1, padding: '4px 6px', fontSize: '0.72rem', fontWeight: 700 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>SHIPPING MODE: <Field value={draft.shippingMode || ''} change={(value) => setField('shippingMode', value)} /></div>
                        <div>DELIVERY TYPE: {draft.deliveryType}</div>
                        <div>TERMS: {draft.terms}</div>
                        <div>PAYMENT TERMS: {draft.paymentTerms}</div>
                    </div>
                </div>
                {type !== 'packing' && (
                    <div style={{ padding: '4px 6px', textAlign: 'center', borderBottom: '2px solid #000' }}>
                        <div style={{ fontWeight: 800, color: '#003366', fontSize: '0.95rem' }}>{draft.personalUse}</div>
                    </div>
                )}
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr>
                            {type === 'packing' ? (
                                <>
                                    <th style={cell}>Marks & Nos.</th>
                                    <th style={cell}>PKG GROSS WT</th>
                                    <th style={cell}>PKG DIMENSION</th>
                                    <th style={cell}>DESCRIPTION</th>
                                </>
                            ) : (
                                <>
                                    <th style={cell}>SL NO</th>
                                    <th style={cell}>DESCRIPTION</th>
                                    <th style={cell}>HS CODE</th>
                                    <th style={cell}>COO</th>
                                    <th style={cell}>QNTY</th>
                                    <th style={cell}>UNIT PRICE (AED)</th>
                                    <th style={cell}>TOTAL AMOUNT (AED)</th>
                                </>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {type === 'packing' ? packingRows.map((row, index) => (
                            <tr key={index}>
                                <td style={{ ...cell, textAlign: 'center', width: '12%' }}>{row.marks}</td>
                                <td style={{ ...cell, textAlign: 'center', width: '16%' }}>{row.weight}</td>
                                <td style={{ ...cell, textAlign: 'center', width: '22%' }}>{row.dimension}</td>
                                <td style={{ ...cell, textAlign: 'left', width: '50%' }}>{row.description}</td>
                            </tr>
                        )) : (draft.items || []).map((item, index) => (
                            <tr key={index}>
                                <td style={{ ...cell, textAlign: 'center' }}>{index + 1}</td>
                                <td style={cell}>{item.description}</td>
                                <td style={{ ...cell, textAlign: 'center' }}>{item.hsCode}</td>
                                <td style={{ ...cell, textAlign: 'center' }}>{item.coo}</td>
                                <td style={{ ...cell, textAlign: 'center' }}>{item.qty}</td>
                                <td style={{ ...cell, textAlign: 'center' }}>{money(item.unitPrice).toFixed(2)}</td>
                                <td style={{ ...cell, textAlign: 'center' }}>{(money(item.qty) * money(item.unitPrice)).toFixed(2)}</td>
                            </tr>
                        ))}
                        {type === 'packing' && (
                            <>
                                <tr>
                                    <td colSpan={4} style={{ ...cell, height: 18, borderLeft: '2px solid #000', borderRight: '2px solid #000' }}></td>
                                </tr>
                                <tr>
                                    <td colSpan={2} style={{ ...cell, textAlign: 'left', fontWeight: 700, padding: '8px 12px', borderRight: 'none' }}>TOTAL PACKAGES:</td>
                                    <td style={{ ...cell, borderLeft: 'none', borderRight: 'none' }}></td>
                                    <td style={{ ...cell, textAlign: 'right', fontWeight: 700, padding: '8px 16px', borderLeft: 'none' }}>{totalPackages} PACKAGES</td>
                                </tr>
                                <tr>
                                    <td colSpan={2} style={{ ...cell, textAlign: 'left', fontWeight: 700, padding: '8px 12px', borderRight: 'none' }}>TOTAL GROSS WEIGHT (KGS):</td>
                                    <td style={{ ...cell, borderLeft: 'none', borderRight: 'none' }}></td>
                                    <td style={{ ...cell, textAlign: 'right', fontWeight: 700, padding: '8px 16px', borderLeft: 'none' }}>{totalGross} KGS</td>
                                </tr>
                            </>
                        )}
                        {type !== 'packing' && (
                            <>
                                <tr>
                                    <td style={cell}></td>
                                    <td style={{ ...cell, color: '#003366', fontWeight: 700 }}>INSURANCE</td>
                                    <td style={cell} colSpan={4}></td>
                                    <td style={{ ...cell, textAlign: 'center' }}>{money(draft.insurance).toFixed(2)}</td>
                                </tr>
                                <tr>
                                    <td style={cell}></td>
                                    <td style={{ ...cell, color: '#003366', fontWeight: 700 }}>FREIGHT</td>
                                    <td style={cell} colSpan={4}></td>
                                    <td style={{ ...cell, textAlign: 'center' }}>{money(draft.freight).toFixed(2)}</td>
                                </tr>
                                {type === 'insurance' && (
                                    <>
                                        <tr>
                                            <td colSpan={6} style={{ ...cell, textAlign: 'center', color: '#003366', fontWeight: 700 }}>TOTAL ( CIF VALUE FOR CUSTOMS PURPOSE )</td>
                                            <td style={{ ...cell, textAlign: 'center', fontWeight: 700 }}>{totalCif.toFixed(2)}</td>
                                        </tr>
                                        <tr>
                                            <td style={cell}></td>
                                            <td style={{ ...cell, color: '#003366', fontWeight: 700 }}>DESTINATION CHARGES</td>
                                            <td style={cell} colSpan={4}></td>
                                            <td style={{ ...cell, textAlign: 'center' }}>{money(draft.destinationCharges).toFixed(2)}</td>
                                        </tr>
                                        <tr>
                                            <td colSpan={6} style={{ ...cell, textAlign: 'center', color: '#003366', fontWeight: 700 }}>TOTAL DDP VALUE</td>
                                            <td style={{ ...cell, textAlign: 'center', fontWeight: 700 }}>{totalDdp.toFixed(2)}</td>
                                        </tr>
                                    </>
                                )}
                                {type !== 'insurance' && (
                                    <tr>
                                        <td colSpan={6} style={{ ...cell, textAlign: 'center', color: '#003366', fontWeight: 700 }}>TOTAL CIF VALUE</td>
                                        <td style={{ ...cell, textAlign: 'center', fontWeight: 700 }}>{totalCif.toFixed(2)}</td>
                                    </tr>
                                )}
                                <tr>
                                    <td colSpan={7} style={{ ...cell, fontWeight: 700 }}>
                                        <span style={{ color: '#003366' }}>Amount in Words : </span>
                                        AE DIRHAMS {amountWords(grandTotal)} ONLY
                                    </td>
                                </tr>
                            </>
                        )}
                    </tbody>
                </table>
            </div>
            {type === 'insurance' && (
                <div style={{ marginTop: 12, padding: 12, background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534', fontSize: '0.85rem' }}>
                    <div><strong>Insurance Coverage:</strong> {draft.coverageNote}</div>
                    <div><strong>Declared Value for Insurance:</strong> {draft.declaredValue}</div>
                </div>
            )}
            <div style={{ textAlign: 'center', marginTop: 16, color: '#94a3b8', fontSize: '0.75rem' }}>This is a computer-generated document. No signature is required.</div>
        </div>
    )
}

const Documents = (props) => {
    const { jobData, formValidator } = props
    const [state,setState] = useState({
        job_id:"",
        customer_document: [],
        store_document: [],
    })
    const [exportDocs, setExportDocs] = useState(props.jobData?.export_documents || [])
    const [previewDoc, setPreviewDoc] = useState(null)
    const [invoiceDraft, setInvoiceDraft] = useState(null)
    const [invoiceEditing, setInvoiceEditing] = useState(false)
    const [approvingId, setApprovingId] = useState("")
    const [savingInvoice, setSavingInvoice] = useState(false)

    useEffect(() => {
        setExportDocs(props.jobData?.export_documents || [])
    }, [props.jobData])

    const openPreview = (doc) => {
        setPreviewDoc(doc)
        setInvoiceEditing(false)
        if (doc && (doc.kind === 'INVOICE' || doc.invoice_type)) {
            const saved = doc.invoice_content
            const hasSaved = saved && (Array.isArray(saved.items) || Array.isArray(saved.packages))
            const fresh = buildInvoiceDraft(props.jobData, doc.invoice_type || 'invoice')
            const draft = hasSaved ? {
                ...fresh,
                shipperName: saved.shipperName || fresh.shipperName,
                shipperAddress: saved.shipperAddress || fresh.shipperAddress,
                shipperCountry: saved.shipperCountry || fresh.shipperCountry,
                shipperPhone: saved.shipperPhone || fresh.shipperPhone,
                invoiceNo: saved.invoiceNo || fresh.invoiceNo,
                invoiceDate: saved.invoiceDate || fresh.invoiceDate,
                consigneeName: saved.consigneeName || fresh.consigneeName,
                consigneeAddress: saved.consigneeAddress || fresh.consigneeAddress,
                consigneeCity: saved.consigneeCity || fresh.consigneeCity,
                consigneeCountry: saved.consigneeCountry || fresh.consigneeCountry,
                consigneePhone: saved.consigneePhone || fresh.consigneePhone,
                consigneeEmail: saved.consigneeEmail || fresh.consigneeEmail,
                shippingMode: saved.shippingMode || fresh.shippingMode,
                items: fresh.items,
                packages: fresh.packages,
            } : fresh
            setInvoiceDraft(applyBookingIdentity(draft, props.jobData))
        } else {
            setInvoiceDraft(null)
        }
    }

    const saveInvoice = () => {
        if (!previewDoc || !invoiceDraft) return
        setSavingInvoice(true)
        JobService.saveExportDocument({
            job_id: props.jobData._id,
            document_id: previewDoc._id,
            invoice_content: invoiceDraft,
        }).then((res) => {
            if (res && res.status) {
                setExportDocs((prev) => prev.map((item) => String(item._id) === String(previewDoc._id) ? { ...item, invoice_content: invoiceDraft } : item))
                setPreviewDoc((prev) => prev ? { ...prev, invoice_content: invoiceDraft } : prev)
                toast.success(res.message || 'Invoice saved')
            } else {
                toast.error(res?.message || 'Could not save the invoice')
            }
        }).catch(() => toast.error('Could not save the invoice'))
        .finally(() => setSavingInvoice(false))
    }

    const approveExportDoc = (doc) => {
        setApprovingId(doc._id)
        JobService.approveExportDocument({ job_id: props.jobData._id, document_id: doc._id }).then((res) => {
            if (res && res.status) {
                setExportDocs((prev) => prev.map((item) => String(item._id) === String(doc._id) ? { ...item, status: 'APPROVED' } : item))
                setPreviewDoc((prev) => prev && String(prev._id) === String(doc._id) ? { ...prev, status: 'APPROVED' } : prev)
                toast.success(res.message || 'Document approved')
            } else {
                toast.error(res?.message || 'Could not approve the document')
            }
        }).catch(() => toast.error('Could not approve the document'))
        .finally(() => setApprovingId(""))
    }

    const isImageFile = (url) => /\.(png|jpe?g|gif|webp)(\?|$)/i.test(url || '')
    const isPdfFile = (url) => /\.pdf(\?|$)/i.test(url || '')
    const isInvoiceDoc = (doc) => doc && (doc.kind === 'INVOICE' || !!doc.invoice_type)
    const uploadDocs = exportDocs.filter((doc) => !isInvoiceDoc(doc))
    const invoiceDocs = exportDocs.filter((doc) => isInvoiceDoc(doc))

    const downloadInvoice = async () => {
        const sheet = document.getElementById('export-invoice-sheet')
        if (!sheet) return
        const html2canvas = (await import('html2canvas')).default
        const { jsPDF } = await import('jspdf')
        const canvas = await html2canvas(sheet, { scale: 2, backgroundColor: '#ffffff' })
        const image = canvas.toDataURL('image/png')
        const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
        const pageWidth = 210
        const pageHeight = 297
        const imageHeight = (canvas.height * pageWidth) / canvas.width
        let heightLeft = imageHeight
        let position = 0
        pdf.addImage(image, 'PNG', 0, position, pageWidth, imageHeight)
        heightLeft -= pageHeight
        while (heightLeft > 0) {
            position = heightLeft - imageHeight
            pdf.addPage()
            pdf.addImage(image, 'PNG', 0, position, pageWidth, imageHeight)
            heightLeft -= pageHeight
        }
        const fileName = `${previewDoc?.document_name || 'Document'}.pdf`
        const blob = pdf.output('blob')
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = fileName
        document.body.appendChild(link)
        link.click()
        link.remove()
        URL.revokeObjectURL(url)
    }

    useEffect(() => {
        setData()
    }, [])
    

    const setData = () => {
        // const { customer_document, store_document } = this.state
        // if ((customer_document.length == 0 || store_document.length == 0)) {
        //     // mayank sir code
        //     // this.setState({
        //     //     customer_document: [...this.props.jobData.customer_document, {
        //     //         document_name: '',
        //     //         document_type: '',
        //     //         document_file: []
        //     //     }],
        //     //     store_document: [...this.props.jobData.store_document, , {
        //     //         document_name: '',
        //     //         document_type: '',
        //     //         document_file: []
        //     //     }]
        //     // })


        // }
        // rizwan code
        setState({
            job_id:props.jobData._id,
            customer_document: props.jobData.document?.customer_document,
            store_document: props.jobData.document?.store_document
        })
    };
    

    const handleInputChage = (event, type) => {
        const { customer_document, store_document } = state
        const { name, value, files } = event.target;
        let list = type == 'customer_document' ? customer_document : store_document
        if(list.length === 0){
            list = [{document_name:"",document_type:"",document:"Document",document_file:[]}]
        }

        if (files) {
            let selectedFile = files;
            const formData = new FormData();

            if (selectedFile) {
                for (const objkey of selectedFile) {
                    formData.append('document', objkey)
                }
            }

            masterService.upload_document(formData).then(res => {
                if (res.status === true) {
                    let arr = list[list.length - 1]['document_file']
                    arr.push(res.data)
                    list[list.length - 1]['document_file'] = arr

                    let filtered = list.filter(function (el) {
                        return el != null;
                    });
                    setState({...state,[type]: filtered })
                    props.onChangeValue({ value: filtered }, 'document', type)
                }
            })
        } 
        else {
            list[list.length - 1][name] = value
            let filtered = list.filter(function (el) {
                return el != null;
            });
            setState({...state,[type]: filtered })
            props.onChangeValue({ value: filtered }, 'document', type)
        }
    }

    const removeDocument = (itemdata,doc_type) => {
        let params = {job_id : state.job_id, document_type:doc_type}
        JobService.deleteJobDocument(params).then((res) => {
            if(res?.status){
                toast.success(res?.message)
            }
        })
        let data = []
        for(let i = 0;i<state[doc_type][0].document_file.length;i++){
            if(itemdata !== state[doc_type][0].document_file[i]){
                data.push(state[doc_type][0].document_file[i])
            }
        }
        let crd = {...state}
        crd[doc_type][0].document_file = data
        setState({...crd })
    }
     

    // render() {
        // const { customer_document, store_document } = this.state
        // const { jobData, formValidator } = this.props
        // if (!jobData?.document || Object.keys(jobData?.document).length === 0) return false

        // console.log("customer_document ....", props)
        // console.log("state ....", state)

        const renderDocRow = (doc) => (
            <div key={doc._id || doc.invoice_type || doc.label} className="d-flex align-items-center justify-content-between border rounded p-2 mb-2">
                <div>
                    <div className="fw-bold">{doc.label || 'Document'}</div>
                    <div className="small text-muted">{doc.document_name}</div>
                    {isInvoiceDoc(doc) && (
                        <span className={`badge ${doc.status === 'APPROVED' ? 'bg-success' : 'bg-warning text-dark'}`}>{doc.status || 'PENDING'}</span>
                    )}
                </div>
                <div className="d-flex" style={{ gap: 8 }}>
                    <Button size="sm" variant="outline-primary" onClick={() => openPreview(doc)}>View</Button>
                    {doc.file_path && (
                        <Button size="sm" variant="outline-secondary" as="a" href={documentViewUrl(doc.file_path)} target="_blank" rel="noreferrer">Download</Button>
                    )}
                    {isInvoiceDoc(doc) && doc.status !== 'APPROVED' && (
                        <Button size="sm" variant="success" disabled={approvingId === doc._id} onClick={() => approveExportDoc(doc)}>
                            {approvingId === doc._id ? 'Approving...' : 'Approve'}
                        </Button>
                    )}
                </div>
            </div>
        )

        return (
            <Card>
                <Card.Header><h4>Documents</h4></Card.Header>
                <Card.Body>
                    <p className="text-muted small">Booking files uploaded with this job. Open a file here to view or download it.</p>
                    {uploadDocs.length === 0 && <div className="text-muted mb-3">No booking documents uploaded.</div>}
                    {uploadDocs.map(renderDocRow)}

                    <Card className="mt-4">
                        <Card.Header><h4 className="mb-0">Export Documents</h4></Card.Header>
                        <Card.Body>
                            <p className="text-muted small">Customs Invoice, INS Invoice, and Packing List are created for every booking, even when no file was uploaded. Approve an invoice to show it on the retailer export documents page.</p>
                            {invoiceDocs.length === 0 && <div className="text-muted mb-3">Invoices are being prepared.</div>}
                            {invoiceDocs.map(renderDocRow)}
                        </Card.Body>
                    </Card>

                    <Modal show={!!previewDoc} onHide={() => { setPreviewDoc(null); setInvoiceDraft(null); setInvoiceEditing(false) }} size="xl" centered>
                        <Modal.Header closeButton>
                            <Modal.Title>{previewDoc?.label || 'Document'}</Modal.Title>
                        </Modal.Header>
                        <Modal.Body style={{ maxHeight: '70vh', overflow: 'auto', background: '#f1f5f9', padding: '8px 12px' }}>
                            {previewDoc && isInvoiceDoc(previewDoc) && invoiceDraft && (
                                <InvoiceSheet draft={invoiceDraft} type={previewDoc.invoice_type || 'invoice'} editing={invoiceEditing} onChange={setInvoiceDraft} />
                            )}
                            {previewDoc && !isInvoiceDoc(previewDoc) && isImageFile(previewDoc.file_path) && (
                                <img src={documentViewUrl(previewDoc.file_path)} alt={previewDoc.document_name} style={{ width: '100%', maxHeight: '70vh', objectFit: 'contain' }} />
                            )}
                            {previewDoc && !isInvoiceDoc(previewDoc) && isPdfFile(previewDoc.file_path) && (
                                <iframe title={previewDoc.document_name} src={documentViewUrl(previewDoc.file_path)} style={{ width: '100%', height: '70vh', border: 0 }} />
                            )}
                            {previewDoc && !isInvoiceDoc(previewDoc) && previewDoc.file_path && !isImageFile(previewDoc.file_path) && !isPdfFile(previewDoc.file_path) && (
                                <a href={documentViewUrl(previewDoc.file_path)} target="_blank" rel="noreferrer">Open document</a>
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => { setPreviewDoc(null); setInvoiceDraft(null); setInvoiceEditing(false) }}>Close</Button>
                            {previewDoc && previewDoc.file_path && !isInvoiceDoc(previewDoc) && <Button variant="primary" as="a" href={documentViewUrl(previewDoc.file_path)} target="_blank" rel="noreferrer">Download</Button>}
                            {previewDoc && isInvoiceDoc(previewDoc) && <Button variant="outline-primary" onClick={downloadInvoice}>Download</Button>}
                            {previewDoc && isInvoiceDoc(previewDoc) && !invoiceEditing && <Button variant="primary" onClick={() => setInvoiceEditing(true)}>Edit</Button>}
                            {previewDoc && isInvoiceDoc(previewDoc) && invoiceEditing && <Button variant="primary" disabled={savingInvoice} onClick={saveInvoice}>{savingInvoice ? 'Saving...' : 'Save'}</Button>}
                            {previewDoc && isInvoiceDoc(previewDoc) && previewDoc.status !== 'APPROVED' && (
                                <Button variant="success" disabled={approvingId === previewDoc._id || savingInvoice} onClick={() => {
                                    setSavingInvoice(true)
                                    JobService.saveExportDocument({
                                        job_id: props.jobData._id,
                                        document_id: previewDoc._id,
                                        invoice_content: invoiceDraft,
                                    }).then((res) => {
                                        if (res && res.status) {
                                            setExportDocs((prev) => prev.map((item) => String(item._id) === String(previewDoc._id) ? { ...item, invoice_content: invoiceDraft } : item))
                                            approveExportDoc({ ...previewDoc, invoice_content: invoiceDraft })
                                        } else {
                                            toast.error(res?.message || 'Could not save the invoice')
                                        }
                                    }).catch(() => toast.error('Could not save the invoice'))
                                    .finally(() => setSavingInvoice(false))
                                }}>Approve</Button>
                            )}
                        </Modal.Footer>
                    </Modal>

                    <Row>
                        <Col>
                            <Card.Title>Customer</Card.Title>
                            <Row>

                                {state?.customer_document?.length > 0 && state?.customer_document.map((item1) => {
                                    return (
                                        item1.document_file.length > 0 &&
                                            item1.document_file.map((item, i) => {
                                                return <div className='mt-3 cursor-pointer w-auto position-relative'>
                                                    <div className='card'>
                                                    <ImCancelCircle className='text-danger position-absolute end-0 fs-5' onClick={() => removeDocument(item,'customer_document')}/>
                                                        <div className='card-body'>
                                                            <div className='d-flex'>
                                                                <div>
                                                                    <div className='fileicon'>
                                                                        <AiOutlineFileDone style={{ fontSize: 30 }} onClick={() =>
                                                                        window.open(`${REACT_APP_API_BASEURL}/uploads/document/${item}`)
                                                } />
                                                                    </div>
                                                                </div>
                                                                <div className='ps-2'>
                                                                    <div>Document Name: </div>
                                                                    <div>{item?.split('.')[0] || 'N/A'}</div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            }
                                            )
                                    )
                                })
                                
                                
                                
                                    }
                            </Row>

                            <div className="d-flex">
                                {/* <div className="position-relative me-2">
                                    <label className="form-label">Doc Name</label>
                                    <input
                                        className="form-control form-control-sm"
                                        name="document_name"
                                        onChange={(e) => this.handleInputChage(e, 'customer_document')}
                                    />
                                </div> */}

                                {/* <div className="position-relative me-2">
                                    <label className="form-label">Doc Type</label>
                                    <input
                                        className="form-control form-control-sm"
                                        name="document_type"
                                        onChange={(e) => this.handleInputChage(e, 'customer_document')}
                                    />
                                </div> */}

                                <Form.Group controlId="formFile" className="mb-3">
                                    <Form.Label>Document</Form.Label>
                                    <Form.Control
                                        type="file"
                                        name="document_file"
                                        // multiple
                                        onChange={(e) => handleInputChage(e, 'customer_document')}
                                    />
                                </Form.Group>
                            </div>
                        </Col>
                    </Row>
                    <Row className='mt-3'>
                        <Col>
                            <Card.Title>Store</Card.Title>
                            <Row>
                                {state && state?.store_document && state.store_document[0]?.document_file.length > 0 && state.store_document[0].document_file.map((item, i) => {
                                    return <div className='mt-3 w-auto cursor-pointer position-relative'>
                                        <div className='card'>
                                        <ImCancelCircle className='text-danger position-absolute end-0 fs-5' onClick={() => removeDocument(item,'store_document')}/>
                                            <div className='card-body'>
                                                <div className='d-flex'>
                                                    <div>
                                                        <div className='fileicon'>
                                                            <AiOutlineFileDone style={{ fontSize: 30 }} onClick={() =>
                                        window.open(`${REACT_APP_API_BASEURL}/uploads/document/${item}`)
                                    } />
                                                        </div>
                                                    </div>
                                                    <div className='ps-2'>
                                                        <div>Document Name: </div>
                                                        <div>{item?.split('.')[0] || 'N/A'}</div>
                                                        {/* <div>Type: {store_document[0].document_file[0] ? store_document[0].document_file[0]
                                                            && store_document[0].document_file[0].split('.').slice(-1) : 'N/A'

                                                        }
                                                        </div> */}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                }

                                )}
                            </Row>
                            <div className="d-flex">
                                {/* <div className="position-relative me-2">
                                    <label className="form-label">Doc Name</label>
                                    <input
                                        className="form-control form-control-sm"
                                        name="document_name"
                                        onChange={(e) => this.handleInputChage(e, 'store_document')}
                                    />
                                </div> */}

                                {/* <div className="position-relative me-2">
                                    <label className="form-label">Doc Type</label>
                                    <input
                                        className="form-control form-control-sm"
                                        name="document_type"
                                        onChange={(e) => this.handleInputChage(e, 'store_document')}
                                    />
                                </div> */}

                                <Form.Group controlId="formFile" className="mb-3">
                                    <Form.Label>Document</Form.Label>
                                    <Form.Control
                                        type="file"
                                        name="document_file"
                                        // multiple
                                        onChange={(e) => handleInputChage(e, 'store_document')}
                                    />
                                </Form.Group>
                            </div>
                        </Col>
                    </Row>

                    <Row>
                        <Col>
                            <Form.Label>Comment</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                name="comments"
                                value={jobData?.document?.comments}
                                onChange={(e) => handleInputChage(e, 'store_document')}
                            />
                        </Col>
                    </Row>

                </Card.Body>
            </Card>

        )
    // }
}
export default Documents
